import { useEffect, useMemo, useState } from 'react';
import type { RoleGrant, RolesResponse } from '@kassa/shared';
import { api, ApiError } from '../../api';

// Rollar (KT-95): hər rolun icazələri qrup üzrə bir yerdə. Kassir üçün tam siyahı: ödəniş növləri, endirim, qaytarma,
// ləğv, nisyə, mədaxil/məxaric, növbə (X/Z), qalıq və maya, başqa kassirin satışı. Sahib rolu dəyişmir.
// Bir kassirə ayrıca qayda lazımdırsa — yeni rol (məs. «Kassir — yalnız nağd»), ayrıca istifadəçi icazəsi yoxdur.

const GROUP_ORDER = ['ödəniş', 'satış', 'kassa', 'müştəri', 'pul', 'anbar', 'kataloq', 'alış', 'təsdiq', 'hesabat', 'maliyyə', 'müqavilə', 'idxal', 'AI', 'tənzimləmə'];
const GROUP_LABEL: Record<string, string> = { ödəniş: 'Ödəniş növləri', satış: 'Satış', kassa: 'Kassa növbəsi', müştəri: 'Müştəri', pul: 'Pul', anbar: 'Anbar' };

export function RolesPanel() {
  const [data, setData] = useState<RolesResponse | null>(null);
  const [roleId, setRoleId] = useState<string>('');
  const [draft, setDraft] = useState<Map<string, RoleGrant['scope']>>(new Map());
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = (select?: string) =>
    api
      .roles()
      .then((d) => {
        setData(d);
        const id = select ?? (roleId || d.roles.find((r) => r.code === 'cashier')?.id || d.roles[0]?.id || '');
        setRoleId(id);
        setDraft(new Map((d.roles.find((r) => r.id === id)?.permissions ?? []).map((g) => [g.code, g.scope])));
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Rollar yüklənmədi'));

  useEffect(() => {
    void load();
    // İlk yükləmə; sonrakılar əməliyyatdan sonra
  }, []);

  const role = data?.roles.find((r) => r.id === roleId) ?? null;
  const groups = useMemo(() => {
    const m = new Map<string, RolesResponse['permissions']>();
    for (const p of data?.permissions ?? []) m.set(p.group, [...(m.get(p.group) ?? []), p]);
    const ord = (g: string) => {
      const i = GROUP_ORDER.indexOf(g);
      return i < 0 ? 999 : i;
    };
    return [...m.entries()].sort((a, b) => ord(a[0]) - ord(b[0]) || a[0].localeCompare(b[0]));
  }, [data]);

  const dirty = role ? JSON.stringify([...draft.keys()].sort()) !== JSON.stringify(role.permissions.map((g) => g.code).sort()) || role.permissions.some((g) => draft.get(g.code) !== g.scope) : false;

  const choose = (id: string) => {
    // Saxlanmamış dəyişiklik itməsin
    if (dirty && id !== roleId && !window.confirm('Saxlanmamış dəyişiklik var. Başqa rola keçilsin?')) return;
    setRoleId(id);
    setNotice(null);
    setDraft(new Map((data?.roles.find((r) => r.id === id)?.permissions ?? []).map((g) => [g.code, g.scope])));
  };

  const toggle = (code: string, on: boolean) => {
    const next = new Map(draft);
    if (on) next.set(code, 'full');
    else next.delete(code);
    setDraft(next);
  };

  // action mesaj və (istəyə görə) seçiləcək rol id-si qaytarır — köhnə roleId-yə qayıtmasın
  async function act(action: () => Promise<{ msg: string; select?: string }>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { msg, select } = await action();
      await load(select ?? roleId);
      setNotice(msg);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Əməliyyat alınmadı');
    } finally {
      setBusy(false);
    }
  }

  const save = () =>
    act(async () => {
      await api.roleSetPermissions(roleId, [...draft.entries()].map(([code, scope]) => ({ code, scope })));
      return { msg: `«${role?.name}» rolu saxlanıldı.` };
    });

  const create = () =>
    act(async () => {
      const name = newName.trim();
      const r = await api.roleCreate(name, role && !role.locked ? role.id : null);
      setNewName('');
      return { msg: `«${name}» rolu yaradıldı${role && !role.locked ? ` («${role.name}» surəti)` : ''}.`, select: r.id };
    });


  return (
    <section className="card" aria-label="Rollar">
      <h2>Rollar</h2>
      <p className="muted small">Kassirin nə edə biləcəyini burada seçin. Bir kassirə ayrıca qayda lazımdırsa — onun üçün yeni rol yaradın.</p>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="ok" role="status">
          {notice}
        </p>
      )}
      {!data && !error && <p className="muted">Yüklənir…</p>}
      {data && (
        <>
          <div className="role-tabs" role="group" aria-label="Rol">
            {data.roles.map((r) => (
              <button key={r.id} type="button" aria-pressed={r.id === roleId} className={r.id === roleId ? 'm-chip m-chip-on' : 'm-chip'} onClick={() => choose(r.id)}>
                {r.name}
                {r.members > 0 ? ` · ${r.members}` : ''}
              </button>
            ))}
          </div>
          {role && (
            <div role="region" aria-label={`«${role.name}» icazələri`}>
              {role.locked && <p className="muted small">Sahib rolu bütün icazələrə malikdir və dəyişmir.</p>}
              {groups.map(([grp, perms]) => (
                <fieldset key={grp} className="perm-group" disabled={role.locked || busy}>
                  <legend>{GROUP_LABEL[grp] ?? grp.charAt(0).toUpperCase() + grp.slice(1)}</legend>
                  {perms.map((p) => (
                    <label key={p.code} className="check-row">
                      <input type="checkbox" checked={draft.has(p.code)} onChange={(e) => toggle(p.code, e.target.checked)} />
                      <span>
                        {p.name}
                        {draft.get(p.code) === 'partial' && <span className="tag">qismən</span>}
                      </span>
                    </label>
                  ))}
                </fieldset>
              ))}
              {!role.locked && (
                <button type="button" className="btn-primary" onClick={save} disabled={busy || !dirty}>
                  Rolu saxla
                </button>
              )}
            </div>
          )}
          <div className="role-new">
            <label className="field">
              Yeni rol{role && !role.locked ? ` («${role.name}» surəti)` : ''}
              <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Məs. Kassir — yalnız nağd" maxLength={60} disabled={busy} />
            </label>
            <button type="button" className="btn-small" onClick={create} disabled={busy || newName.trim().length === 0}>
              Rolu əlavə et
            </button>
          </div>
        </>
      )}
    </section>
  );
}
